import React from 'react';
import { Instagram, Mail, MessageCircle } from 'lucide-react';
import StarterCta from '../components/StarterCta';
import { Bloom, Sprig } from '../components/home/Florals';
import { Fade } from '../components/content/Content';

const steps = [
  { n: '1', title: 'Tell us what brings you here', body: 'A short form: your name, WhatsApp number and what is on your mind.' },
  { n: '2', title: 'Receive your personalised PDF', body: 'Prepared for you. A small ₹9 token covers it and helps us make time for those who are ready.' },
  { n: '3', title: 'Choose your time', body: 'Pick a slot for your 15-minute call with Madhulika.' },
  { n: '4', title: 'We remind you', body: 'A gentle reminder before your call. Need a different time? Just message us on WhatsApp.' },
];

const contacts = [
  { name: 'Instagram', icon: Instagram, label: 'Daily guidance', href: 'https://www.instagram.com/mystic_prana_by_madhulika?igsh=MTZwaWhtZGg2OWdvYw%3D%3D&utm_source=qr' },
  { name: 'WhatsApp', icon: MessageCircle, label: 'Chat with Madhulika', href: 'https://wa.me/919868911575' },
  { name: 'Email', icon: Mail, label: 'Inquiries', href: 'mailto:mystikpranna@gmail.com' },
];

const Home: React.FC = () => (
  <>
    {/* 1. Hero */}
    <section className="relative pt-28 pb-14 px-6 overflow-hidden bg-gradient-to-b from-lavender-100/70 via-cream to-cream">
      <Sprig className="absolute -left-4 top-24 h-64 text-lavender-300 opacity-60 hidden md:block" />
      <div className="max-w-6xl mx-auto grid md:grid-cols-[1.15fr_0.85fr] gap-10 items-center relative">
        <div className="text-center md:text-left">
          <p className="text-xs uppercase tracking-[0.3em] text-lavender-700 mb-4">Mystic Prana</p>
          <h1 className="font-fraunces text-5xl sm:text-6xl font-medium leading-[1.05] text-plum mb-5">
            Heal with <span className="text-lavender-700">Madhulika</span>
          </h1>
          <p className="text-lg text-plum-soft max-w-md mx-auto md:mx-0 mb-7">Discover clarity and balance through ancient wisdom.</p>
          <div className="flex flex-wrap items-center justify-center md:justify-start gap-x-5 gap-y-3">
            <StarterCta source="hero" />
            <a href="#how-it-works" className="text-sm text-lavender-700 underline underline-offset-4 hover:text-plum">See how it works</a>
          </div>
        </div>

        {/* Portrait: transparent cutout, so it sits straight on the page. Edges fade out softly. */}
        <div className="relative mx-auto w-72 sm:w-80 md:w-full md:max-w-sm">
          <div className="absolute inset-x-6 top-10 bottom-0 rounded-full bg-lavender-200/60 blur-3xl" aria-hidden="true" />
          <Bloom className="absolute top-0 -right-4 w-20 text-lavender-400 opacity-70 z-10" />
          <img
            src={`${process.env.PUBLIC_URL}/images/madhulika-portrait.webp`}
            alt="Madhulika, Tarot reader and energy healer"
            width={900}
            height={1380}
            fetchPriority="high"
            data-testid="portrait"
            className="relative w-full h-auto"
            style={{
              WebkitMaskImage: 'linear-gradient(to bottom, #000 62%, transparent), linear-gradient(to right, transparent, #000 16%, #000 90%, transparent)',
              maskImage: 'linear-gradient(to bottom, #000 62%, transparent), linear-gradient(to right, transparent, #000 16%, #000 90%, transparent)',
              WebkitMaskComposite: 'source-in',
              maskComposite: 'intersect',
            }}
          />
        </div>
      </div>
    </section>

    {/* 2. How it works */}
    <section id="how-it-works" className="px-6 py-14">
      <Fade className="max-w-6xl mx-auto">
        <h2 className="font-fraunces text-3xl sm:text-4xl font-medium text-center mb-2">
          How we <span className="text-lavender-700">begin</span>
        </h2>
        <p className="text-center text-plum-soft mb-10">Four gentle steps to your first conversation with Madhulika.</p>
        <ol className="grid sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-8">
          {steps.map((s) => (
            <li key={s.n}>
              <span className="font-fraunces text-4xl text-lavender-500">{s.n}</span>
              <div className="h-px w-12 bg-lavender-300 my-3" />
              <h3 className="font-fraunces text-xl mb-1">{s.title}</h3>
              <p className="text-sm text-plum-soft leading-relaxed">{s.body}</p>
            </li>
          ))}
        </ol>

      </Fade>
    </section>

    {/* 3. About */}
    <section id="about" className="px-6 py-14 bg-lavender-50">
      <Fade className="max-w-2xl mx-auto text-center">
        <div>
          <h2 className="font-fraunces text-3xl sm:text-4xl font-medium mb-4">About <span className="text-lavender-700">Madhulika</span></h2>
          <p className="text-plum-soft leading-relaxed mb-3">
            I am a Tarot reader and energy healer offering soulful guidance without superstition. For me, Tarot reveals your inner truth, while chakra healing restores your energetic balance.
          </p>
          <p className="text-plum-soft leading-relaxed">
            Every session is a quiet pause: a space to reconnect with yourself and leave with grounded guidance for the questions on your mind.
          </p>
        </div>
      </Fade>
    </section>

    {/* 4. Start */}
    <section id="connect" className="relative px-6 py-14 text-center overflow-hidden">
      <Sprig className="absolute -right-4 bottom-0 h-48 text-lavender-300 opacity-60 hidden md:block" />
      <Fade className="max-w-2xl mx-auto relative">
        <h2 className="font-fraunces text-3xl sm:text-4xl font-medium mb-3">Ready when you are</h2>
        <p className="text-plum-soft mb-6">Share what is on your mind, and Madhulika will take it from there.</p>
        <StarterCta source="connect" />
        <ul className="mt-8 flex flex-wrap justify-center gap-x-8 gap-y-3 text-sm">
          {contacts.map((c) => (
            <li key={c.name}>
              <a href={c.href} target={c.href.startsWith('http') ? '_blank' : undefined} rel="noopener noreferrer" className="inline-flex items-center gap-2 text-plum-soft hover:text-lavender-700 transition-colors">
                <c.icon className="w-4 h-4" aria-hidden="true" />
                <span>{c.name}</span>
                <span className="text-plum-soft">· {c.label}</span>
              </a>
            </li>
          ))}
        </ul>
      </Fade>
    </section>
  </>
);

export default Home;
